import React from "react";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { BonusApplicationHistoryItem, BonusApplicationComment } from "../../../services/bonus/types";

interface BonusApplicationHistoryProps {
  historyItems: BonusApplicationHistoryItem[]; comments: BonusApplicationComment[];
  historyAccordionExpanded: boolean; onAccordionToggle: () => void;
  showAccordion?: boolean; showTitle?: boolean;
}

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  CCM:   { bg: "#DBEAFE", text: "#1E40AF" }, CM3:  { bg: "#CCFBF1", text: "#0F766E" },
  PM:    { bg: "#CCFBF1", text: "#0F766E" }, SPM:  { bg: "#CFFAFE", text: "#0E7490" },
  DCCM:  { bg: "#E0E7FF", text: "#4338CA" }, DM:   { bg: "#F3E8FF", text: "#7E22CE" },
  VP:    { bg: "#FEF3C7", text: "#B45309" }, Ops:  { bg: "#FFEDD5", text: "#C2410C" },
  Admin: { bg: "#FEE2E2", text: "#B91C1B" },
};

const getRolePill = (role?: string) => {
  const c = ROLE_COLORS[role ?? ""] ?? { bg: "var(--bg-chip)", text: "var(--text-secondary)" };
  return { background: c.bg, color: c.text, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20 };
};

const fmtDate = (date: string | Date | null) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const BonusApplicationHistory: React.FC<BonusApplicationHistoryProps> = ({ historyItems, comments, historyAccordionExpanded, onAccordionToggle, showAccordion = true, showTitle = true }) => {
  const getComments = (historyId: number) => comments.filter(c => c.historyId === historyId);

  const renderContent = () => {
    if (historyItems.length === 0) return <p style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>No history available</p>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {historyItems.map(item => {
          const itemComments = getComments(item.historyId);
          return (
            <div key={item.historyId} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              {/* Timeline dot */}
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--brand-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                <svg style={{ width: 11, height: 11, color: "#fff" }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: "0 0 2px", fontWeight: 600, color: "var(--text-primary)", fontSize: 14 }}>{item.currentStatusName?.replace(/_/g, " ")}</p>
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>{item.completedByEmail || item.changedBy || "Unknown"}</p>
                {item.completedOn && <p style={{ margin: "1px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{fmtDate(item.completedOn)}</p>}

                {(item.changedBy || item.changedOn) && (
                  <div style={{ marginTop: 8, background: "var(--bg-surface-raised)", borderLeft: "3px solid var(--border-hover)", borderRadius: "0 6px 6px 0", padding: "8px 12px" }}>
                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Changes Made</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12, color: "var(--text-secondary)" }}>
                      {item.previousStatusName && <span><strong>Previous:</strong> {item.previousStatusName.replace(/_/g, " ")}</span>}
                      {item.changedBy && <span><strong>Changed By:</strong> {item.changedBy}</span>}
                      {item.changedOn && <span><strong>Changed On:</strong> {fmtDate(item.changedOn)}</span>}
                    </div>
                  </div>
                )}

                {itemComments.length > 0 && (
                  <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: "2px solid var(--border-default)", display: "flex", flexDirection: "column", gap: 8 }}>
                    {itemComments.map(c => (
                      <div key={c.commentId} style={{ background: "var(--bg-chip)", border: "1px solid var(--border-default)", borderRadius: 8, padding: "10px 12px" }}>
                        <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>{c.commentText}</p>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>— {c.commentedBy || c.commentedByEmail}</p>
                      </div>
                    ))}
                  </div>
                )}

                {item.isBulkAction && item.bulkActionPerformedBy && (
                  <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 6 }}>
                    <span style={{ fontSize: 12, color: "#7E22CE", fontWeight: 500 }}>Bulk action by: {item.bulkActionPerformedBy}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!showAccordion) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {showTitle && <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Review History</h3>}
      {renderContent()}
    </div>
  );

  return (
    <Accordion expanded={historyAccordionExpanded} onChange={onAccordionToggle} sx={{ boxShadow: "none", "&:before": { display: "none" }, mb: 2 }}>
      <AccordionSummary expandIcon={<ChevronDownIcon style={{ width: 18, height: 18, color: "var(--text-muted)" }} />}
        sx={{ background: "var(--bg-surface-raised)", borderRadius: "8px", "&:hover": { background: "var(--bg-chip)" }, minHeight: 48, "& .MuiAccordionSummary-content": { margin: "12px 0" } }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--text-primary)" }}>Review History</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "16px", background: "var(--bg-surface)" }}>{renderContent()}</AccordionDetails>
    </Accordion>
  );
};

export default BonusApplicationHistory;